import { CommonModule, DatePipe } from '@angular/common';
import { Component, OnInit, effect, inject, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ToastService } from 'angular-toastify';
import { CookieService } from 'ngx-cookie-service';
import { PssLoadingComponent } from '../../../../../tools/template/pss/pss-loading/pss-loading.component';
import { InvoiceHeader } from '../../../../../core/models/invoice.model';
import {
  InvoiceDashboardStat,
  InvoiceRecentActivityView,
  InvoiceTrendChartPoint,
} from '../../../../../core/models/finance/invoice-list.model';
import { MapperInvoiceListService } from '../../../../../core/mappers/finance/mapper-invoice-list.service';
import { ApiInvoiceListService } from '../../../../../core/services/api/finance/api-invoice-list.service';
import { SiteStateService } from '../../../../../core/services/site-state.service';
import { VatSettingService } from '../../../../../core/services/vat-setting.service';
import { ModalInvoiceDetailComponent } from '../../../modals/finance/invoice/modal-invoice-detail/modal-invoice-detail.component';

@Component({
  selector: 'app-invoice-list',
  standalone: true,
  imports: [
    FormsModule,
    CommonModule,
    PssLoadingComponent,
    ModalInvoiceDetailComponent,
  ],
  templateUrl: './invoice-list.component.html',
  styleUrl: './invoice-list.component.css',
  providers: [DatePipe],
})
export class InvoiceListComponent implements OnInit {
  readonly siteState = inject(SiteStateService);
  private readonly router = inject(Router);
  private readonly apiService = inject(ApiInvoiceListService);
  readonly vatSetting = inject(VatSettingService);
  private readonly mapper = inject(MapperInvoiceListService);
  private readonly toast = inject(ToastService);
  private readonly cookieService = inject(CookieService);
  private readonly datePipe = inject(DatePipe);

  isModalDetailOpen = false;
  selectedInvoiceId = 0;
  invoices: InvoiceHeader[] = [];
  expandedInvoiceId = signal<number | null>(null);

  showDashboard = true;
  dashboardStats: InvoiceDashboardStat[] = [];
  recentActivity: InvoiceRecentActivityView[] = [];
  trendChartPoints: InvoiceTrendChartPoint[] = [];
  totalAmount = 0;
  paidAmount = 0;
  pendingAmount = 0;
  overdueAmount = 0;

  chartMode: 'daily' | 'monthly' = 'daily';
  dailySubMode: 'default' | 'custom' = 'default';
  customStartDate = '';
  customEndDate = '';
  chartType: 'line' | 'bar' = 'line';
  selectedMonthStart = new Date().toISOString().slice(0, 7);
  selectedMonthEnd = new Date().toISOString().slice(0, 7);
  yAxisLabels: number[] = [];
  currentMonthPoints = '';
  currentMonthAreaPoints = '';
  currentMonthDots: Array<{ x: number; y: number; label: string; subLabel: string; value: number; color: string }> = [];
  chartTotalCount = 0;

  searchText = '';
  searchType: 'customerName' | 'invoiceNo' = 'customerName';
  isSearchTypeDropdownOpen = false;
  filterStatus: number | 'all' = 'all';
  filterMinTotal: number | null = null;
  filterMinRemaining: number | null = null;

  currentPage = 1;
  pageSize = 10;
  pageSizeOptions: number[] = [10, 20, 50, 100];
  totalItems = 0;
  totalPages = 1;

  /** yyyy-MM-dd — same pattern as transaction-parking */
  startDate = '';
  endDate = '';

  isLoading = false;
  isDashboardLoading = false;
  isTrendLoading = false;
  lastFetchedTime: Date | null = null;
  userId = 0;

  sortColumn = 'id';
  sortDirection: 'asc' | 'desc' = 'desc';

  constructor() {
    const today = this.toApiDate(this.getTodayDate());
    this.startDate = today;
    this.endDate = today;

    const userIdLocal = localStorage.getItem('userId');
    this.userId = userIdLocal
      ? Number(userIdLocal)
      : Number(this.cookieService.get('user_id'));

    effect(() => {
      const site = this.siteState.selectedSite();
      if (!site?.id || !this.userId) return;

      untracked(() => {
        this.currentPage = 1;
        this.loadTable();
        this.loadMarketingDashboard();
        this.loadTrendChart();
      });
    });
  }

  ngOnInit(): void {
    if (!this.userId) {
      const userIdLocal = localStorage.getItem('userId');
      this.userId = userIdLocal
        ? Number(userIdLocal)
        : Number(this.cookieService.get('user_id'));
    }
    if (this.selectedSiteId && this.userId) {
      this.loadTable();
      this.loadMarketingDashboard();
      this.loadTrendChart();
    }
  }

  get selectedSiteId(): number {
    return this.siteState.selectedSite()?.id ?? 0;
  }

  toggleDashboard(): void {
    this.showDashboard = !this.showDashboard;
  }

  showTableFilters = false;

  toggleTableFilters(): void {
    this.showTableFilters = !this.showTableFilters;
  }

  get tableDateRangeLabel(): string {
    return `${this.formatDateDisplay(this.startDate)} - ${this.formatDateDisplay(this.endDate)}`;
  }

  get searchPlaceholder(): string {
    return this.searchType === 'customerName' ? 'ค้นหาชื่อลูกค้า...' : 'ค้นหาเลขที่ใบแจ้งหนี้...';
  }

  /**
   * Toggles row expansion to show inline invoice details.
   * Prevents expansion if the click target is an interactive button or icon.
   */
  toggleRow(id: number, event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (target.closest('button') || target.closest('a') || target.closest('.favorite-btn')) {
      return;
    }
    this.expandedInvoiceId.update((v) => (v === id ? null : id));
  }

  sortData(column: string): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'desc';
    }
    this.currentPage = 1;
    this.loadTable();
  }

  get pagesArray(): number[] {
    const maxVisibleButtons = 5;
    let startPage = Math.max(1, this.currentPage - 2);
    let endPage = Math.min(this.totalPages, startPage + maxVisibleButtons - 1);

    if (endPage - startPage < maxVisibleButtons - 1) {
      startPage = Math.max(1, endPage - maxVisibleButtons + 1);
    }

    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  }

  /**
   * Returns the starting item index of the current page.
   */
  get startItemIndex(): number {
    if (this.totalItems === 0) return 0;
    return (this.currentPage - 1) * this.pageSize + 1;
  }

  /**
   * Returns the ending item index of the current page.
   */
  get endItemIndex(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalItems);
  }

  setPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.loadTable();
    }
  }

  onPageSizeChange(event: Event): void {
    const element = event.target as HTMLSelectElement;
    this.pageSize = Number(element.value);
    this.currentPage = 1;
    this.loadTable();
  }

  onFilterChange(): void {
    this.currentPage = 1;
    this.loadTable();
  }

  onSearchChange(): void {
    this.currentPage = 1;
  }

  onClickSearch(): void {
    this.currentPage = 1;
    this.loadTable();
  }

  onRefresh(): void {
    this.searchText = '';
    this.filterStatus = 'all';
    this.filterMinTotal = null;
    this.filterMinRemaining = null;
    this.searchType = 'customerName';
    const today = this.toApiDate(this.getTodayDate());
    this.startDate = today;
    this.endDate = today;
    this.currentPage = 1;
    this.loadTable();
    this.loadMarketingDashboard();
    this.loadTrendChart();
  }

  onDateChange(): void {
    this.normalizeDateRange();
    this.currentPage = 1;
    this.loadTable();
    this.loadMarketingDashboard();
    this.loadTrendChart();
  }

  private getTodayDate(): Date {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }

  private toApiDate(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  /** Ensure start/end dates are never empty and start <= end */
  private normalizeDateRange(): void {
    const today = this.toApiDate(this.getTodayDate());
    if (!this.startDate?.trim()) this.startDate = today;
    if (!this.endDate?.trim()) this.endDate = today;
    if (this.startDate > this.endDate) {
      this.endDate = this.startDate;
    }
  }

  formatDateDisplay(date: Date | string | null | undefined): string {
    if (!date) return '-';
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}/.test(date)) {
      const [year, month, day] = date.slice(0, 10).split('-');
      return `${day}/${month}/${year}`;
    }
    const d = date instanceof Date ? date : new Date(date);
    if (isNaN(d.getTime())) return '-';
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  }

  private formatDateString(date: Date | string): string {
    if (typeof date === 'string') return date.trim();
    return this.toApiDate(date);
  }

  toggleSearchTypeDropdown(): void {
    this.isSearchTypeDropdownOpen = !this.isSearchTypeDropdownOpen;
  }

  selectSearchType(type: 'customerName' | 'invoiceNo'): void {
    this.searchType = type;
    this.isSearchTypeDropdownOpen = false;
    this.onSearchChange();
  }

  get currentSearchTypeLabel(): string {
    switch (this.searchType) {
      case 'customerName': return 'ชื่อลูกค้า';
      case 'invoiceNo': return 'เลขที่ใบแจ้งหนี้';
      default: return 'ค้นหา';
    }
  }

  selectStatus(status: number | 'all'): void {
    this.filterStatus = this.filterStatus === status ? 'all' : status;
    this.currentPage = 1;
    this.loadTable();
  }

  navigateToCreate(): void {
    this.router.navigate(['/invoice-detail']);
  }

  navigateToDetail(id: number): void {
    this.selectedInvoiceId = id;
    this.isModalDetailOpen = true;
  }

  navigateToEdit(id: number): void {
    this.router.navigate(['/invoice-detail', id]);
  }

  navigateToPayment(invoice: InvoiceHeader): void {
    this.selectedInvoiceId = invoice.id;
    this.isModalDetailOpen = true;
  }

  closeDetailModal(): void {
    this.isModalDetailOpen = false;
    this.selectedInvoiceId = 0;
  }

  saveDetailModal(): void {
    this.isModalDetailOpen = false;
    this.selectedInvoiceId = 0;
    this.loadTable();
    this.loadMarketingDashboard();
    this.loadTrendChart();
  }

  getDisplayStatus(inv: InvoiceHeader): number {
    return this.mapper.resolveDisplayStatus(inv);
  }

  getStatusConfig(status: number) {
    switch (status) {
      case 1:
        return {
          label: 'รอชำระ',
          bsClass: 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800',
          icon: 'hourglass_top',
          iconClass: 'text-amber-500'
        };
      case 2:
        return {
          label: 'ชำระแล้ว',
          bsClass: 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800',
          icon: 'check_circle',
          iconClass: 'text-green-500'
        };
      case 3:
        return {
          label: 'ยกเลิก',
          bsClass: 'bg-rose-50 dark:bg-rose-900/30 text-rose-700 dark:text-rose-400 border-rose-200 dark:border-rose-800',
          icon: 'cancel',
          iconClass: 'text-rose-500'
        };
      case 4:
        return {
          label: 'เกินกำหนด',
          bsClass: 'bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700',
          icon: 'event_busy',
          iconClass: 'text-slate-400'
        };
      default:
        return {
          label: 'ไม่ระบุ',
          bsClass: 'bg-slate-50 dark:bg-slate-800 text-slate-500 dark:text-slate-400 border-slate-200 dark:border-slate-700',
          icon: 'help_outline',
          iconClass: 'text-slate-400'
        };
    }
  }

  loadTable(): void {
    const siteId = this.selectedSiteId;
    if (!siteId || !this.userId) {
      this.invoices = [];
      this.totalItems = 0;
      this.totalPages = 1;
      return;
    }

    this.isLoading = true;
    this.normalizeDateRange();
    const status = this.mapper.mapStatusFilter(this.filterStatus);

    this.apiService.getInvoiceForTable({
      siteId,
      userId: this.userId,
      page: this.currentPage,
      pageSize: this.pageSize,
      startDate: this.formatDateString(this.startDate),
      endDate: this.formatDateString(this.endDate),
      searchField: this.searchText.trim() ? this.mapper.mapSearchField(this.searchType) : undefined,
      searchText: this.searchText.trim() || undefined,
      sortColumn: this.mapper.mapSortColumn(this.sortColumn),
      sortDirection: this.sortDirection,
      status: status ?? 'all',
      minTotal: this.filterMinTotal,
      minRemaining: this.filterMinRemaining,
    }).subscribe({
      next: (res) => {
        const mapped = this.mapper.mapTableFromDto(res);
        this.invoices = mapped.items;
        this.totalItems = mapped.total;
        this.totalPages = mapped.totalPages || Math.ceil(mapped.total / (this.pageSize || 1));
        this.lastFetchedTime = new Date();
        this.isLoading = false;
      },
      error: (err) => {
        console.error('[API Error] getInvoiceForTable:', err);
        this.invoices = [];
        this.totalItems = 0;
        this.totalPages = 1;
        this.isLoading = false;
        this.toast.error('การดึงข้อมูลใบแจ้งหนี้ล้มเหลว');
      }
    });
  }

  loadMarketingDashboard(): void {
    const siteId = this.selectedSiteId;
    if (!siteId || !this.userId) {
      this.resetDashboardView();
      return;
    }

    this.isDashboardLoading = true;
    this.normalizeDateRange();
    const startStr = this.formatDateString(this.startDate);
    const endStr = this.formatDateString(this.endDate);
    this.apiService.getInvoiceMarketingDashboard(
      siteId,
      this.userId,
      startStr,
      endStr
    ).subscribe({
      next: (res) => {
        this.applyMarketingDashboard(this.mapper.mapMarketingDashboard(res));
        this.isDashboardLoading = false;
      },
      error: (err) => {
        console.error('[API Error] getInvoiceMarketingDashboard:', err);
        this.resetDashboardView();
        this.isDashboardLoading = false;
      }
    });
  }

  loadTrendChart(): void {
    const siteId = this.selectedSiteId;
    if (!siteId || !this.userId) {
      this.resetTrendView();
      return;
    }

    if (this.chartMode === 'monthly' && !this.validateMonthlyRange()) {
      return;
    }

    this.isTrendLoading = true;
    const query = this.buildTrendChartQuery(siteId);

    this.apiService.getInvoiceTrendChart(query).subscribe({
      next: (res) => {
        const view = this.mapper.mapTrendChart(res);
        this.trendChartPoints = view.points;
        this.chartTotalCount = view.totalCount;
        this.renderTrendChart();
        this.isTrendLoading = false;
      },
      error: (err) => {
        console.error('[API Error] getInvoiceTrendChart:', err);
        this.resetTrendView();
        this.isTrendLoading = false;
        const message = err?.error ?? err?.message;
        if (typeof message === 'string' && message.trim()) {
          this.toast.warn(message);
        }
      }
    });
  }

  private buildTrendChartQuery(siteId: number) {
    if (this.chartMode === 'monthly') {
      return {
        siteId,
        userId: this.userId,
        mode: 'monthly' as const,
        monthStart: this.selectedMonthStart,
        monthEnd: this.selectedMonthEnd,
      };
    }

    const query = {
      siteId,
      userId: this.userId,
      mode: 'daily' as const,
      dailySubMode: this.dailySubMode,
    };

    if (this.dailySubMode === 'custom') {
      return {
        ...query,
        startDate: this.customStartDate,
        endDate: this.customEndDate,
      };
    }

    return query;
  }

  private applyMarketingDashboard(view: ReturnType<MapperInvoiceListService['mapMarketingDashboard']>): void {
    this.dashboardStats = view.stats;
    this.totalAmount = view.summary.totalAmount;
    this.paidAmount = view.summary.paidAmount;
    this.pendingAmount = view.summary.pendingAmount;
    this.overdueAmount = view.summary.overdueAmount;
    this.recentActivity = view.recentActivity.map(item => ({
      ...item,
      timeAgo: this.getTimeAgo(item.invoiceDate ?? null),
      color: this.getRandomColor(item.id),
    }));
  }

  private resetTrendView(): void {
    this.trendChartPoints = [];
    this.chartTotalCount = 0;
    this.currentMonthPoints = '';
    this.currentMonthAreaPoints = '';
    this.currentMonthDots = [];
  }

  private resetDashboardView(): void {
    this.dashboardStats = [];
    this.totalAmount = 0;
    this.paidAmount = 0;
    this.pendingAmount = 0;
    this.overdueAmount = 0;
    this.recentActivity = [];
    this.resetTrendView();
  }

  formatDate(date: Date | string): string {
    return this.formatDateString(date);
  }

  getTimeAgo(dateInput: string | Date | null | undefined): string {
    if (!dateInput) return '-';
    const date = new Date(dateInput);
    if (isNaN(date.getTime())) return '-';
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    let interval = seconds / 31536000;
    if (interval > 1) return Math.floor(interval) + ' ปีที่แล้ว';
    interval = seconds / 2592000;
    if (interval > 1) return Math.floor(interval) + ' เดือนที่แล้ว';
    interval = seconds / 86400;
    if (interval > 1) return Math.floor(interval) + ' วันที่แล้ว';
    interval = seconds / 3600;
    if (interval > 1) return Math.floor(interval) + ' ชั่วโมงที่แล้ว';
    interval = seconds / 60;
    if (interval > 1) return Math.floor(interval) + ' นาทีที่แล้ว';
    return 'เพิ่งสร้าง';
  }

  renderTrendChart(): void {
    const dataPoints = this.trendChartPoints.map(point => point.count);

    const rawMax = Math.max(...dataPoints, 5);
    const maxVal = Math.ceil(rawMax / 4) * 4;
    this.yAxisLabels = [0, maxVal * 0.25, maxVal * 0.5, maxVal * 0.75, maxVal];

    const getCoordinate = (val: number, index: number, total: number) => {
      if (total <= 1) return { x: 50, y: 100 - ((val / maxVal) * 100) };
      const x = 2 + (index / (total - 1)) * 96;
      const y = 100 - ((val / maxVal) * 100);
      return { x, y };
    };

    const pointsObj = dataPoints.map((val, i) => getCoordinate(val, i, dataPoints.length));
    this.currentMonthPoints = pointsObj.map(p => `${p.x},${p.y}`).join(' ');

    this.currentMonthDots = pointsObj.map((p, i) => {
      const point = this.trendChartPoints[i];
      return {
        x: p.x,
        y: p.y,
        label: point?.label ?? '',
        subLabel: point?.subLabel ?? '',
        value: dataPoints[i],
        color: this.getChartColor(i),
      };
    });

    if (pointsObj.length > 0) {
      const firstPoint = pointsObj[0];
      const lastPoint = pointsObj[pointsObj.length - 1];
      this.currentMonthAreaPoints = `
          ${firstPoint.x},100
          ${this.currentMonthPoints}
          ${lastPoint.x},100
        `;
    } else {
      this.currentMonthAreaPoints = '';
    }
  }

  getRandomColor(id: number): string {
    const colors = ['bg-blue-500', 'bg-green-500', 'bg-yellow-500', 'bg-purple-500', 'bg-pink-500', 'bg-indigo-500'];
    return colors[id % colors.length];
  }

  getChartColor(index: number): string {
    const colors = ['#fbbf24', '#f59e0b', '#34d399', '#60a5fa', '#f87171', '#818cf8', '#a78bfa', '#f472b6'];
    return colors[index % colors.length];
  }

  setChartMode(mode: 'daily' | 'monthly'): void {
    this.chartMode = mode;
    if (mode === 'monthly') {
      const today = new Date();
      const currentMonthStub = today.toISOString().slice(0, 7);
      const prevMonthDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      this.selectedMonthStart = prevMonthDate.toISOString().slice(0, 7);
      this.selectedMonthEnd = currentMonthStub;
    }
    this.loadTrendChart();
  }

  setChartType(type: 'line' | 'bar'): void {
    this.chartType = type;
  }

  onChartMonthChange(): void {
    if (this.chartMode !== 'monthly') return;
    if (!this.validateMonthlyRange()) return;
    this.loadTrendChart();
  }

  private validateMonthlyRange(): boolean {
    const [startYear, startMonth] = this.selectedMonthStart.split('-').map(Number);
    const [endYear, endMonth] = this.selectedMonthEnd.split('-').map(Number);
    const monthSpan = (endYear - startYear) * 12 + (endMonth - startMonth) + 1;

    if (monthSpan > 12) {
      this.toast.warn('ช่วงรายเดือนต้องไม่เกิน 12 เดือน');
      const end = new Date(endYear, endMonth - 1, 1);
      const start = new Date(end.getFullYear(), end.getMonth() - 11, 1);
      this.selectedMonthStart = start.toISOString().slice(0, 7);
    }

    if (monthSpan < 1) {
      this.selectedMonthEnd = this.selectedMonthStart;
      return false;
    }

    return true;
  }

  setDailySubMode(mode: 'default' | 'custom'): void {
    this.dailySubMode = mode;
    if (mode === 'custom') {
      if (!this.customStartDate) {
        const e = new Date();
        const s = new Date();
        s.setDate(e.getDate() - 9);
        this.customStartDate = this.formatDate(s);
        this.customEndDate = this.formatDate(e);
      }
      this.onCustomDateChange();
      return;
    }
    this.loadTrendChart();
  }

  get maxCustomDate(): string {
    if (!this.customStartDate) return '';
    const d = new Date(this.customStartDate);
    d.setDate(d.getDate() + 10);
    return this.formatDate(d);
  }

  resetCustomDate(): void {
    const e = new Date();
    const s = new Date();
    s.setDate(e.getDate() - 9);
    this.customEndDate = this.formatDate(e);
    this.customStartDate = this.formatDate(s);
    this.loadTrendChart();
  }

  onCustomDateChange(): void {
    if (!this.customStartDate || !this.customEndDate) return;
    const s = new Date(this.customStartDate);
    const e = new Date(this.customEndDate);
    if (e < s) this.customEndDate = this.customStartDate;

    const diffDays = Math.ceil((e.getTime() - s.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays > 10) {
      this.toast.warn('แสดงผลได้สูงสุด 10 วัน');
      const newEnd = new Date(s);
      newEnd.setDate(s.getDate() + 9);
      this.customEndDate = this.formatDate(newEnd);
    }
    this.loadTrendChart();
  }
}
